import React, { useRef, createRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { kebabCase, uniqueId } from 'lodash';

import FeaturesCarouselTextSliderSlide from '~/components/features-carousel/FeaturesCarouselTextSliderSlide';

import useWindowSize from '~/hooks/useWindowSize';

import { transition } from '~/styles/transition';
import { calcVw } from '~/styles/helpers';

import { scrollToElement } from '~/utilities/helpers';

const Wrapper = styled.div`
    display: flex;
    position: relative;
    justify-content: ${({ noButtons }) =>
        noButtons ? 'flex-end' : 'space-between'};
    align-items: center;
    column-gap: 35px;
    flex-direction: column;
    padding-bottom: 60px;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        flex-direction: row;
        margin-top: -100vh;
        padding-bottom: 0;
    }
`;

const DotsWrapper = styled.div`
    padding: 60px 0;
    width: 100%;
    display: flex;
    justify-content: center;
    column-gap: 50px;
    z-index: 2;
    order: 2;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        order: 1;
        padding-left: 14px;
        position: sticky;
        top: 0;
        bottom: auto;
        height: 100vh;
        align-self: flex-start;
        flex-direction: column;
        gap: 40px;
        justify-content: center;
        flex: 1 0 40px;
        align-items: flex-start;
        transform: translateY(0);
    }
`;

const Dot = styled.button`
    background-color: white;
    height: 18px;
    width: 18px;
    border-radius: 50%;
    position: relative;
    ${transition({}, 'background-color')};

    &:hover,
    &:focus {
        background-color: ${({ theme }) => theme.colors.primary.yellow};
    }

    ${({ isSelected, theme }) =>
        isSelected &&
        `
 
        &::before {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 46px;
                height: 46px;
                border-radius: 50%;
                background-color: ${theme.colors.primary.yellow};
                z-index: ${theme.zIndex.backgroundColor};
            }

            &::after {
                content: '';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background-color: ${theme.colors.primary.red};
                z-index: ${theme.zIndex.backgroundColor};
            }
  `}
`;

const StyledSlider = styled.div`
    flex: 1 1 100%;
    width: calc(${calcVw(100)} - 2 * ${({ theme }) => theme.layout.gutter});
    overflow: hidden;
    text-align: center;
    position: relative;
    display: flex;
    order: 1;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        display: block;
        width: auto;
        flex: 0 1 530px;
        order: 2;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeDesktop}) {
        flex: 0 1 650px;
    }
`;

const TextSlider = ({
    slides,
    featuresCarouselState,
    featuresCarouselDispatch
}) => {
    const slideRefs = useRef(slides.map(() => createRef()));
    const sliderRef = useRef(null);
    const sizes = useWindowSize();

    const scrollToSlide = useCallback(
        (index) => {
            if (slideRefs.current && slideRefs.current[index].current) {
                if (sizes.includes('tablet')) {
                    scrollToElement(slideRefs.current[index].current);
                } else {
                    sliderRef.current?.scroll({
                        top: 0,
                        left: (window.innerWidth - 20) * index,
                        behavior: 'smooth'
                    });
                }
            }
        },
        [sizes, slideRefs, sliderRef]
    );

    return (
        <Wrapper noButtons={slides.length < 2}>
            {slides.length > 1 ? (
                <DotsWrapper>
                    {slides.map(({ title }, index) => (
                        <Dot
                            key={uniqueId(kebabCase(title))}
                            onClick={() => scrollToSlide(index)}
                            isSelected={
                                index === featuresCarouselState.currentSlide
                            }
                        />
                    ))}
                </DotsWrapper>
            ) : null}
            <StyledSlider ref={sliderRef}>
                {slides.map(({ title, description }, index) => (
                    <FeaturesCarouselTextSliderSlide
                        key={kebabCase(`${title}-${description}`)}
                        ref={slideRefs.current[index]}
                        slideIndex={index}
                        {...{
                            title,
                            description,
                            featuresCarouselState,
                            featuresCarouselDispatch
                        }}
                    />
                ))}
            </StyledSlider>
        </Wrapper>
    );
};

TextSlider.propTypes = {
    slides: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            description: PropTypes.string
        })
    ),
    featuresCarouselState: PropTypes.object.isRequired,
    featuresCarouselDispatch: PropTypes.func.isRequired
};

export default TextSlider;
